import React from "react";
import { toast } from "sonner";
import {
  Receipt, Download, Calendar, Loader2, RefreshCw, TrendingUp, FileText, FileSpreadsheet,
} from "lucide-react";
import { businessApi } from "../../lib/api";

const fmt = (n) => Number(n || 0).toLocaleString("da-DK", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const currentMonth = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);  // Default to previous month
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function BusinessInvoicePage() {
  const [month, setMonth] = React.useState(currentMonth());
  const [basis, setBasis] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await businessApi().get("/business/invoice-basis", { params: { month } });
      setBasis(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke hente faktura-grundlag");
    } finally {
      setLoading(false);
    }
  }, [month]);

  React.useEffect(() => { load(); }, [load]);

  const download = async (fmt2) => {
    try {
      const res = await businessApi().get(`/business/invoice-basis/${month}/${fmt2}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `faktura_grundlag_${month}.${fmt2}`; a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Download fejlede");
    }
  };

  const t = basis?.totals || {};
  return (
    <div className="space-y-6" data-testid="business-invoice-page">
      <header>
        <div className="text-xs uppercase tracking-widest text-brand-red font-bold">SPRINT 2H</div>
        <h1 className="font-display font-bold text-3xl text-brand-dark mt-1">Faktura-grundlag</h1>
        <p className="text-brand-text2 mt-2">Månedligt grundlag med antal forsendelser, gebyrer og total. Eksportér som PDF, Excel eller CSV til din bogføring.</p>
      </header>

      <div className="flex flex-wrap gap-3 items-center bg-white border border-brand p-4">
        <label className="text-sm font-semibold text-brand-dark inline-flex items-center gap-2"><Calendar size={14}/> Måned:</label>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="input-flat py-1.5 max-w-[180px]" data-testid="biz-invoice-month"/>
        <button onClick={load} className="text-sm text-brand-text2 hover:text-brand-dark inline-flex items-center gap-1" data-testid="biz-invoice-reload">
          <RefreshCw size={14}/> Opdater
        </button>
        <div className="ml-auto flex gap-2">
          <button onClick={() => download("pdf")} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white bg-brand-red hover:opacity-90" data-testid="biz-invoice-pdf">
            <FileText size={12}/> PDF
          </button>
          <button onClick={() => download("xlsx")} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-brand-dark bg-brand-bg2 border border-brand hover:bg-white" data-testid="biz-invoice-xlsx">
            <FileSpreadsheet size={12}/> Excel
          </button>
          <button onClick={() => download("csv")} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-brand-dark bg-brand-bg2 border border-brand hover:bg-white" data-testid="biz-invoice-csv">
            <Download size={12}/> CSV
          </button>
        </div>
      </div>

      {loading ? <div className="text-center py-12 text-brand-text2 inline-flex items-center justify-center gap-2"><Loader2 className="animate-spin"/> Indlæser...</div>
      : !basis ? <div className="border border-brand bg-white p-12 text-center text-brand-text2">Ingen data</div>
      : (
        <>
          {/* Header stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Total forsendelser" value={t.total_shipments} icon={Receipt}/>
            <StatCard label="Same Day" value={t.same_day_count} accent="red"/>
            <StatCard label="Next Day" value={t.next_day_count} accent="brand"/>
            <StatCard label="Total inkl. moms" value={`${fmt(t.total_inc_vat_dkk)} kr`} accent="green" icon={TrendingUp}/>
          </div>

          {/* Breakdown */}
          <div className="bg-white border border-brand">
            <table className="w-full text-sm">
              <thead className="bg-brand-bg2 text-xs uppercase text-brand-text2">
                <tr>
                  <th className="px-4 py-2 text-left">Linje</th>
                  <th className="px-4 py-2 text-right">Antal</th>
                  <th className="px-4 py-2 text-right">Beløb (kr ex moms)</th>
                </tr>
              </thead>
              <tbody>
                <Line label="Same Day forsendelser" count={t.same_day_count} amount={t.same_day_revenue_dkk}/>
                <Line label="Next Day forsendelser" count={t.next_day_count} amount={t.next_day_revenue_dkk}/>
                {t.manual_label_fee_total_dkk > 0 && <Line label="Manuelle label-fees" amount={t.manual_label_fee_total_dkk}/>}
                {t.minimum_pickup_fee_dkk > 0 && <Line label="Min. pickup-fee" amount={t.minimum_pickup_fee_dkk}/>}
                {t.failed_delivery_fee_total_dkk > 0 && <Line label="Mislykket levering (gebyr)" count={t.failed_delivery_fee_count} amount={t.failed_delivery_fee_total_dkk}/>}
                {t.redelivery_fee_total_dkk > 0 && <Line label="Genlevering (gebyr)" count={t.redelivery_fee_count} amount={t.redelivery_fee_total_dkk}/>}
                {t.return_fee_total_dkk > 0 && <Line label="Returnering (gebyr)" count={t.return_fee_count} amount={t.return_fee_total_dkk}/>}
                <Line label={`Energi/miljø-tillæg (${t.energy_surcharge_pct}%)`} amount={t.energy_surcharge_dkk}/>
                <tr className="border-t-2 border-brand-dark">
                  <td className="px-4 py-2 font-semibold">Subtotal ex moms</td>
                  <td></td>
                  <td className="px-4 py-2 text-right font-semibold">{fmt(t.subtotal_ex_vat_dkk)} kr</td>
                </tr>
                <Line label={`Moms (${t.vat_pct}%)`} amount={t.vat_dkk}/>
                <tr className="border-t-2 border-brand-red bg-brand-bg2">
                  <td className="px-4 py-2 font-bold text-brand-red text-lg">Total inkl. moms</td>
                  <td></td>
                  <td className="px-4 py-2 text-right font-bold text-brand-red text-lg">{fmt(t.total_inc_vat_dkk)} kr</td>
                </tr>
              </tbody>
            </table>
          </div>

          {t.waived_failed_count > 0 && (
            <div className="border-l-4 border-green-500 bg-green-50 p-3 text-sm text-green-900">
              <b>{t.waived_failed_count}</b> mislykket(de) levering(er) er eftergivet (gebyr ikke opkrævet).
            </div>
          )}

          {/* Shipments table (first 50) */}
          <div className="bg-white border border-brand">
            <div className="px-4 py-2 bg-brand-bg2 text-xs uppercase text-brand-text2 font-semibold">Inkluderede forsendelser ({basis.shipment_refs?.length || 0})</div>
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-white sticky top-0 text-brand-text2">
                  <tr>
                    <th className="px-3 py-2 text-left">Shipment</th>
                    <th className="px-3 py-2 text-left">Ref</th>
                    <th className="px-3 py-2 text-left">Service</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-right">Pris</th>
                    <th className="px-3 py-2 text-right">Gebyrer</th>
                  </tr>
                </thead>
                <tbody>
                  {(basis.shipment_refs || []).slice(0, 200).map((s) => {
                    const fees = (s.failed_fee || 0) + (s.redelivery_fee || 0) + (s.return_fee || 0);
                    return (
                      <tr key={s.id} className="border-t border-brand">
                        <td className="px-3 py-1.5 font-mono">{s.shipment_number}</td>
                        <td className="px-3 py-1.5">{s.customer_reference || "—"}</td>
                        <td className="px-3 py-1.5">{s.product === "same_day_b2b" ? "SD" : "ND"}</td>
                        <td className="px-3 py-1.5">{s.status}</td>
                        <td className="px-3 py-1.5 text-right">{fmt(s.price_dkk)} kr</td>
                        <td className="px-3 py-1.5 text-right">{fees ? `${fmt(fees)} kr` : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, accent = "gray", icon: Icon }) {
  const colors = { gray: "bg-white border-brand", red: "bg-red-50 border-red-300", green: "bg-green-50 border-green-300", brand: "bg-brand-bg2 border-brand" };
  return (
    <div className={`border p-4 ${colors[accent]}`}>
      <div className="flex justify-between items-start mb-1">
        <span className="text-xs uppercase tracking-wider text-brand-text2">{label}</span>
        {Icon && <Icon size={14} className="text-brand-text2"/>}
      </div>
      <div className="text-2xl font-bold text-brand-dark">{value ?? 0}</div>
    </div>
  );
}

function Line({ label, count, amount }) {
  return (
    <tr className="border-t border-brand">
      <td className="px-4 py-1.5">{label}</td>
      <td className="px-4 py-1.5 text-right text-brand-text2">{count ?? ""}</td>
      <td className="px-4 py-1.5 text-right">{Number(amount || 0).toLocaleString("da-DK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr</td>
    </tr>
  );
}
